import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";

// Open modals, top last: only the top one reacts to Escape, and the page
// scroll is unlocked when the last one closes.
const modalStack = [];
// Body overflow before the first modal opened (e.g. already locked by a MUI dialog)
let bodyOverflowBeforeLock = "";

// Keeps a size inside the viewport (16px gutter) while honouring the caller's value
const clampToViewport = (value, viewport) => {
  if (value === undefined || value === null || value === "auto" || value === "none") {
    return viewport;
  }
  const css = typeof value === "number" ? `${value}px` : value;
  return `min(${css}, ${viewport})`;
};

// WizardStep component for step progression
const WizardStep = ({ step, currentStep, label }) => {
  const isActive = step === currentStep;
  const isCompleted = step < currentStep;

  return (
    <div className="flex items-center min-w-0">
      <div
        className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-medium ${isCompleted || isActive
          ? "bg-primary text-white"
          : "bg-gray-200 text-gray-600"
          }`}
      >
        {step}
      </div>
      <span
        // On phones only the current step keeps its label, so the row never overflows
        className={`ml-2 text-sm font-medium truncate ${isActive ? "text-primary-strong" : `hidden sm:inline ${isCompleted ? "text-gray-900" : "text-gray-500"}`
          }`}
      >
        {label}
      </span>
    </div>
  );
};

const WizardSteps = ({ steps, currentStep, className = "" }) => (
  <div className={`flex items-center justify-between gap-2 sm:gap-4 ${className}`}>
    {steps.map((stepLabel, index) => (
      <React.Fragment key={index}>
        <WizardStep step={index + 1} currentStep={currentStep} label={stepLabel} />
        {index < steps.length - 1 && <div className="flex-1 min-w-4 h-px bg-gray-200" />}
      </React.Fragment>
    ))}
  </div>
);

/**
 * Shared modal shell: header (title or custom header + close button), a body that is
 * the only scrolling area, and an optional footer. It renders in a portal on top of
 * the page, fits the viewport (16px gutter) and stacks above the modal it is opened from.
 */
const Modal = ({
  open = false,
  onClose,
  title,
  header,
  // Accessible name when a custom `header` replaces `title` (aria-labelledby needs the title)
  ariaLabel,
  children,
  footer,
  width = "100%",
  height = "auto",
  minWidth = "auto",
  minHeight = "auto",
  maxWidth = "90vw",
  maxHeight = "90dvh",
  className = "",
  showCloseButton = true,
  closeOnBackdrop = true,
  style = {},
  disableBackdrop = false,
  disableCentering = false,
  reduceBackdrop = false,
  wizardSteps = null,
  currentStep = null,
}) => {
  const { t } = useTranslation();
  const dialogRef = React.useRef(null);
  const titleId = React.useId();

  // Read through a ref: callers often pass an inline onClose
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Element focused before opening, captured on the opening render (before any child autoFocus)
  const wasOpenRef = React.useRef(false);
  const returnFocusRef = React.useRef(null);
  if (open && !wasOpenRef.current && typeof document !== "undefined") {
    returnFocusRef.current = document.activeElement;
  }
  wasOpenRef.current = open;

  // Stack registration, Escape, scroll lock and focus restore
  React.useEffect(() => {
    if (!open) return undefined;

    const entry = {};
    if (modalStack.length === 0) bodyOverflowBeforeLock = document.body.style.overflow;
    modalStack.push(entry);
    document.body.style.overflow = "hidden";
    const previouslyFocused = returnFocusRef.current;

    // Move focus into the dialog unless a child already took it (autoFocus)
    if (dialogRef.current && !dialogRef.current.contains(document.activeElement)) {
      dialogRef.current.focus({ preventScroll: true });
    }

    const handleEscape = (e) => {
      // Skip Escape already handled inside the modal (e.g. a mention dropdown)
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (modalStack[modalStack.length - 1] !== entry) return;
      onCloseRef.current?.();
    };
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      const index = modalStack.indexOf(entry);
      if (index !== -1) modalStack.splice(index, 1);
      if (modalStack.length === 0) document.body.style.overflow = bodyOverflowBeforeLock;
      if (previouslyFocused && typeof previouslyFocused.focus === "function" && previouslyFocused.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open]);

  // Handlers
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && closeOnBackdrop) {
      onClose?.();
    }
  };

  if (!open) return null;

  // Callers that place the dialog themselves (e.g. a popover-like panel)
  const customPosition = disableCentering || style.left !== undefined || style.top !== undefined;
  const hasWizard = Boolean(wizardSteps && currentStep);
  const hasHeader = Boolean(header || title || hasWizard);

  const closeButton = showCloseButton && (
    <button
      type="button"
      onClick={onClose}
      aria-label={t("common:close")}
      className="shrink-0 -mr-2 -mt-1 p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-primary"
    >
      <FaTimes className="w-4 h-4" />
    </button>
  );

  const dialog = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : ariaLabel}
      tabIndex={-1}
      className={`bg-white rounded-2xl shadow-xl flex flex-col outline-none pointer-events-auto ${customPosition ? "fixed" : "relative"} ${className}`}
      style={{
        width,
        height,
        minWidth,
        minHeight,
        // Containing block for `position: fixed` children (inline editors, overlays), as before
        transform: "translateZ(0)",
        ...style,
        maxWidth: clampToViewport(style.maxWidth ?? maxWidth, "calc(100vw - 32px)"),
        maxHeight: clampToViewport(style.maxHeight ?? maxHeight, "calc(100dvh - 32px)"),
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      {hasHeader ? (
        <div className="shrink-0 flex items-start gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            {header ? (
              header
            ) : (
              <>
                {title && (
                  <h2 id={titleId} className="text-lg font-semibold text-gray-900 break-words">
                    {title}
                  </h2>
                )}
                {hasWizard && (
                  <WizardSteps
                    steps={wizardSteps}
                    currentStep={currentStep}
                    className={title ? "mt-4" : ""}
                  />
                )}
              </>
            )}
          </div>
          {closeButton}
        </div>
      ) : (
        showCloseButton && <div className="absolute top-3 right-4 z-10">{closeButton}</div>
      )}

      {/* Content: the only scrolling area */}
      <div className="flex-1 min-h-0 min-w-0 px-5 sm:px-6 py-5 overflow-y-auto overscroll-contain">
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-gray-100">
          {footer}
        </div>
      )}
    </div>
  );

  return createPortal(
    // One layer for backdrop and dialog: later modals stack above earlier ones
    <div
      className={`fixed inset-0 ${customPosition ? "" : "flex items-center justify-center p-4"} ${disableBackdrop ? "pointer-events-none" : ""}`}
      style={{ zIndex: "calc(var(--z-modal) + 1)" }} // 1301: the context menu (--z-popover) stays above
    >
      {!disableBackdrop && (
        <div
          aria-hidden="true"
          className={`absolute inset-0 ${reduceBackdrop ? "bg-black/10" : "bg-black/50 backdrop-blur-sm"}`}
          onClick={handleBackdropClick}
        />
      )}
      {dialog}
    </div>,
    document.body
  );
};

export default Modal;
